import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Bell, Check, ArrowLeft } from 'lucide-react';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import { usePlatformFlags } from '@/context/PlatformFlagsContext';
import YouTubePreview from '@/components/YouTubePreview';
import TranslateInline, { TranslateBar } from '@/components/TranslateInline';
import VoiceInput from '@/components/VoiceInput/VoiceInput';
import { getInputLang } from '@/utils/translatePref';

interface Props {
  bookingId: string;
  tableName?: string;
  /** Branch type so the quick reasons match the venue (bar drinks vs salon towels vs grocery aisles). */
  branchType?: { code?: string; label?: string } | null;
  /** When present, fetch the branch's configured ACTIVE reasons and use them instead of the hardcoded sets. */
  branchId?: string;
  /** Open straight into the song-request panel (e.g. when the Concierge hands off "request a song"). */
  openSongs?: boolean;
  /**
   * Re-establish the table session and return the fresh bookingId (or null if unrecoverable).
   * Supplied by the QR landing; when a call is refused because the session drifted we recover
   * silently instead of showing a guest an error they can do nothing about.
   */
  onSessionInvalid?: () => Promise<string | null>;
  onClose: () => void;
}

interface Call {
  id: string;
  bookingId?: string | null;
  status: 'new' | 'approved' | 'preparing' | 'in_progress' | 'served' | 'cancelled';
  requestText: string;
  assignedStaffName?: string | null;
  createdAt: string;
  /** Set when this ticket is actually a jukebox SONG request (not a staff call) — used to exclude it. */
  songState?: string | null;
  mediaUrl?: string | null;
  /** Queue position + friendly ETA (from the backend) while the call is still waiting. */
  aheadCount?: number;
  etaMinutes?: number;
  /** >0 once the call was auto-escalated for waiting past the SLA. */
  priority?: number;
}

/**
 * Song requests are stored as service tickets on the same booking and ride the SAME realtime events
 * as staff calls. They must never be treated as a "Call staff" ticket (they'd render a phantom
 * "Staff is on the way" card and pollute "Your requests"), so filter them out everywhere `call`/
 * `allCalls` are populated. A ticket is a song when it carries a songState or a mediaUrl.
 */
const isSongTicket = (t: any): boolean => t?.songState != null || !!t?.mediaUrl;

type Reason = { type: string; label: string; emoji: string };

/** A quick song request ticket (gated behind the global `quick_songs` platform flag). */
type SongState = 'queued' | 'playing' | 'played';
type SongTicket = { id: string; mediaTitle?: string; mediaThumbnail?: string; songState?: SongState; votes?: number };

/** Light client-side check that a pasted string looks like a YouTube link before we hit the server. */
const isYouTubeUrl = (s: string) =>
  /^(https?:\/\/)?(www\.|m\.)?(youtube\.com\/(watch\?|shorts\/|live\/|embed\/|playlist\?)|youtu\.be\/)\S+/i.test(s.trim());

/** Pull the 11-char video id out of the common YouTube link shapes so we can embed a preview.
 *  Supports youtube.com/watch?v=ID, youtu.be/ID, /shorts/ID, /embed/ID, /live/ID. Returns null if none. */
const getYouTubeVideoId = (s: string): string | null => {
  const m = s
    .trim()
    .match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([\w-]{11})/i);
  return m ? m[1] : null;
};

/** Human label for the current song ticket state. */
const songStatusLabel = (st?: SongState) =>
  st === 'playing' ? '🎶 Now playing' : st === 'played' ? '✅ Played — thanks!' : "🎵 Queued — we'll play it soon";

/** Quick reasons — one tap sends the call. Beats a one-way hardware pager: the customer says *why*.
 *  The set is chosen per branch type so a salon guest never sees "Order drinks / Another round". */

/** Bar / pub / lounge — drink-led service (rounds, bar orders). */
const BAR_REASONS: Reason[] = [
  { type: 'waiter', label: 'Call staff', emoji: '🙋' },
  { type: 'order', label: 'Order drinks', emoji: '🍺' },
  { type: 'another_round', label: 'Another round', emoji: '🔁' },
  { type: 'food', label: 'Order food', emoji: '🍔' },
  { type: 'menu', label: 'Menu', emoji: '📋' },
  { type: 'water', label: 'Water / refill', emoji: '💧' },
  { type: 'ice', label: 'Ice', emoji: '🧊' },
  { type: 'napkins', label: 'Napkins / cutlery', emoji: '🍴' },
  { type: 'clean', label: 'Clean table', emoji: '🧽' },
  { type: 'bill', label: 'The bill', emoji: '🧾' },
  { type: 'manager', label: 'Manager', emoji: '🧑‍💼' },
  { type: 'help', label: 'Help', emoji: '❓' },
];

/** Restaurant / café / diner — food-led service (no bar "rounds" / "order drinks"). */
const RESTAURANT_REASONS: Reason[] = [
  { type: 'waiter', label: 'Call staff', emoji: '🙋' },
  { type: 'food', label: 'Order food', emoji: '🍔' },
  { type: 'menu', label: 'Menu', emoji: '📋' },
  { type: 'water', label: 'Water / refill', emoji: '💧' },
  { type: 'napkins', label: 'Napkins / cutlery', emoji: '🍴' },
  { type: 'clean', label: 'Clean table', emoji: '🧽' },
  { type: 'bill', label: 'The bill', emoji: '🧾' },
  { type: 'manager', label: 'Manager', emoji: '🧑‍💼' },
  { type: 'help', label: 'Help', emoji: '❓' },
];

/** Salon / spa / barber / beauty — grooming service at the chair. */
const SALON_REASONS: Reason[] = [
  { type: 'waiter', label: 'Call staff', emoji: '🙋' },
  { type: 'services', label: 'Services / menu', emoji: '💇' },
  { type: 'consult', label: 'Style advice', emoji: '💬' },
  { type: 'water', label: 'Water / drink', emoji: '💧' },
  { type: 'towel', label: 'Towel', emoji: '🧖' },
  { type: 'magazine', label: 'Magazine', emoji: '📖' },
  { type: 'touchup', label: 'Touch-up', emoji: '✂️' },
  { type: 'wash', label: 'Hair wash', emoji: '🚿' },
  { type: 'bill', label: 'The bill', emoji: '🧾' },
  { type: 'manager', label: 'Manager', emoji: '🧑‍💼' },
  { type: 'help', label: 'Help', emoji: '❓' },
];

/** Grocery / mart — assistance in the aisles. */
const GROCERY_REASONS: Reason[] = [
  { type: 'waiter', label: 'Call staff', emoji: '🙋' },
  { type: 'find', label: 'Find an item', emoji: '🔎' },
  { type: 'price', label: 'Price check', emoji: '🏷️' },
  { type: 'assist', label: 'Need assistance', emoji: '🛒' },
  { type: 'bags', label: 'Carry bags', emoji: '🛍️' },
  { type: 'bill', label: 'Billing / checkout', emoji: '🧾' },
  { type: 'manager', label: 'Manager', emoji: '🧑‍💼' },
  { type: 'help', label: 'Help', emoji: '❓' },
];

/** Pick the reason set that matches the branch's business type. Default is the food-led restaurant set
 *  (safe — no bar-only "order drinks / another round"); a bar/pub is detected explicitly for those. */
function reasonsForBranchType(bt?: { code?: string; label?: string } | null): Reason[] {
  const k = `${bt?.code || ''} ${bt?.label || ''}`.toLowerCase();
  const has = (...ws: string[]) => ws.some((w) => k.includes(w));
  if (has('salon', 'saloon', 'spa', 'barber', 'beauty')) return SALON_REASONS;
  if (has('grocery', 'mart', 'supermarket', 'kirana')) return GROCERY_REASONS;
  if (has('bar', 'pub', 'lounge', 'club', 'brew', 'tavern')) return BAR_REASONS;
  return RESTAURANT_REASONS;
}

const isOpen = (s: Call['status']) => s === 'new' || s === 'approved' || s === 'preparing' || s === 'in_progress';

/**
 * The server refused because the signed-in identity doesn't own this booking — the table session
 * drifted (guest checked out, or the token was replaced). Recoverable by re-provisioning.
 */
const isSessionMismatch = (e: unknown): boolean => {
  const status = (e as any)?.status;
  const msg = String((e as any)?.message || '').toLowerCase();
  // Match on the message as well as the status: older backends flattened this to a 400, and a bare
  // 403 elsewhere (a disabled feature, say) must not be mistaken for an ended table session.
  return msg.includes('booking is not yours') || msg.includes('session has ended') || status === 409;
};

/**
 * Guests are standing at a table, not reading a stack trace. "This booking is not yours" is a
 * server-side ownership check leaking into a bar; say something they can act on instead.
 */
const friendlyCallError = (e: unknown): string => {
  if (isSessionMismatch(e)) return 'Your table session has ended. Please scan the QR code again.';
  const msg = e instanceof Error ? e.message : '';
  return msg && !/^http error/i.test(msg) ? msg : 'Could not place the call. Please try again, or ask a staff member.';
};

/** Map a ticket status to the compact pill shown in the "Your requests" list. */
const statusPill = (s: Call['status']): { label: string; bg: string; color: string; border: string } => {
  switch (s) {
    case 'in_progress':
      return { label: '🔵 Staff on the way', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
    case 'served':
      return { label: '✅ Completed', bg: '#ecfdf5', color: '#065f46', border: '#6ee7b7' };
    case 'cancelled':
      return { label: 'Cancelled', bg: '#f3f4f6', color: '#9ca3af', border: '#e5e7eb' };
    default: // new / approved / preparing
      return { label: '🟡 Sent', bg: '#fffbeb', color: '#b45309', border: '#fde68a' };
  }
};

/**
 * "Call staff" — the software table-call button. Sends a live request to the staff assigned to this
 * table (their phones + the big-screen board) and shows the acknowledgement coming back:
 * Ringing → Staff is on the way → Served. Only usable while the customer is checked in at the table.
 */
const CallStaff: React.FC<Props> = ({ bookingId, tableName, branchType, branchId, openSongs, onSessionInvalid, onClose }) => {
  // Data-driven reasons: fetched from the branch's configured ACTIVE set. `null` = not loaded yet.
  const [dataReasons, setDataReasons] = useState<Reason[] | null>(null);

  // Fetch the branch's configured reasons; fall back to the hardcoded set if empty/failed/absent.
  useEffect(() => {
    if (!branchId) { setDataReasons(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getTableCallReasons(branchId);
        if (cancelled) return;
        const list = ((res as any)?.data?.reasons || []) as Array<{ id: string; code?: string; label: string; emoji?: string }>;
        const mapped: Reason[] = list
          .filter((r) => r && (r.code || r.id))
          .map((r) => ({ type: (r.code || r.id) as string, label: r.label, emoji: r.emoji || '🔔' }));
        setDataReasons(mapped.length ? mapped : []);
      } catch {
        if (!cancelled) setDataReasons([]); // signal "loaded, use fallback"
      }
    })();
    return () => { cancelled = true; };
  }, [branchId]);

  // Use the fetched reasons when non-empty; otherwise the hardcoded per-branch-type fallback.
  const REASONS = useMemo(
    () => (dataReasons && dataReasons.length ? dataReasons : reasonsForBranchType(branchType)),
    [dataReasons, branchType]
  );
  // Primary (wide) reason: prefer 'waiter'/'staff', else the first reason.
  const primaryType = useMemo(() => {
    const primary = REASONS.find((r) => r.type === 'waiter' || r.type === 'staff') || REASONS[0];
    return primary?.type;
  }, [REASONS]);
  const loadingReasons = !!branchId && dataReasons === null;
  const [call, setCall] = useState<Call | null>(null);
  // Full ticket list for THIS booking (most-recent-first when rendered) — powers the "Your requests"
  // status list. Kept live off the SAME getServiceCalls fetch + realtime subscription as `call`.
  const [allCalls, setAllCalls] = useState<Call[]>([]);
  const [sending, setSending] = useState<string | null>(null);
  const [manualText, setManualText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Array<{ id: string; senderType: string; senderName: string; messageText: string; createdAt: string }>>([]);
  const [msgInput, setMsgInput] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  // Same AI-translation availability the main chat uses, so the call thread gets translate + voice too.
  const [translationAvailable, setTranslationAvailable] = useState(false);
  useEffect(() => {
    if (!branchId) { setTranslationAvailable(false); return; }
    let alive = true;
    apiService.getTranslationAvailableForBranch(branchId)
      .then((ok) => { if (alive) setTranslationAvailable(!!ok); })
      .catch(() => { if (alive) setTranslationAvailable(false); });
    return () => { alive = false; };
  }, [branchId]);

  // Lock the page behind this modal so the background doesn't scroll/move on mobile.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape (keyboard / accessibility).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Post-service feedback (rate + tip + Google-review funnel), shown on the "Served" card.
  const [fbRating, setFbRating] = useState(0);
  const [fbComment, setFbComment] = useState('');
  const [fbTip, setFbTip] = useState<number | null>(null);
  const [fbSubmitting, setFbSubmitting] = useState(false);
  const [fbResult, setFbResult] = useState<null | { reviewUrl: string | null }>(null);
  const submitFeedback = async () => {
    if (!fbRating || fbSubmitting) return;
    setFbSubmitting(true);
    try {
      const res = await apiService.submitServiceFeedback({
        bookingId,
        ticketId: call?.id,
        rating: fbRating,
        comment: fbComment.trim() || undefined,
        tipAmount: fbTip || undefined,
      });
      setFbResult({ reviewUrl: (res as any)?.data?.reviewUrl || null });
    } catch {
      setFbResult({ reviewUrl: null });
    } finally {
      setFbSubmitting(false);
    }
  };
  const resetFeedback = () => { setFbResult(null); setFbRating(0); setFbComment(''); setFbTip(null); };

  // Auto-scroll the per-call chat to the newest message so replies don't hide below the fold.
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  // Quick song request. `usePlatformFlags()` reads from context; outside the provider it returns the
  // DEFAULT_STATE (all flags false), so this is safe even if CallStaff is ever rendered standalone.
  const songsEnabled = usePlatformFlags().quick_songs;
  const [songOpen, setSongOpen] = useState(!!openSongs);
  const [songUrl, setSongUrl] = useState('');
  const [songSending, setSongSending] = useState(false);
  const [songError, setSongError] = useState<string | null>(null);
  const [songTicket, setSongTicket] = useState<SongTicket | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  // When the song panel is open we show ONLY the song UI — the reasons grid + manual row are hidden.
  const showSongPanel = songsEnabled && songOpen;
  // Video id (if the current input is a valid YouTube link) drives the "▶ Preview" button + embed.
  const songVideoId = useMemo(() => getYouTubeVideoId(songUrl), [songUrl]);

  const myUserId = useMemo(() => {
    const { accessToken } = apiService.getTokens();
    return accessToken ? getUserIdFromToken(accessToken) : null;
  }, []);

  // Merge a ticket into the "Your requests" list (insert new, patch existing) — reused everywhere
  // `call` is updated so the status list stays in lock-step with the active-call card.
  const upsertCall = (t: Call) => {
    setAllCalls((prev) => {
      const idx = prev.findIndex((c) => c.id === t.id);
      if (idx === -1) return [t, ...prev];
      const next = prev.slice();
      next[idx] = { ...next[idx], ...t };
      return next;
    });
  };

  // Load any call already in flight for this table.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getServiceCalls();
        if (cancelled) return;
        const mine = ((res as any)?.data || []).filter((c: Call) => c.bookingId === bookingId && !isSongTicket(c));
        setAllCalls(mine as Call[]);
        const active = mine.find((c: Call) => isOpen(c.status)) || null;
        if (active) setCall(active);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  // Live acknowledgement: staff moving the ticket updates the customer instantly.
  useEffect(() => {
    if (!myUserId) return;
    const unsub = subscribeToChannel(
      `user:${myUserId}`,
      ['service_ticket:updated', 'service_ticket:created'] as unknown as readonly string[],
      (_event, data) => {
        if (!data?.id || data.bookingId !== bookingId) return;
        if (isSongTicket(data)) return; // a song ticket is not a staff call — ignore it here
        upsertCall(data as Call); // keep the "Your requests" list live off the same event
        setCall((prev) => {
          // Adopt this ticket if it's ours; drop it once it's resolved so the buttons come back.
          if (prev && prev.id !== data.id && isOpen(prev.status)) return prev;
          // Socket payloads don't carry the computed ETA — keep the last known values (the 4s poll
          // refreshes them) so the ETA chip doesn't flicker away on every status update.
          const d = data as Call;
          return prev && prev.id === d.id
            ? { ...d, aheadCount: d.aheadCount ?? prev.aheadCount, etaMinutes: d.etaMinutes ?? prev.etaMinutes }
            : d;
        });
      }
    );
    return () => { try { unsub(); } catch { /* noop */ } };
  }, [myUserId, bookingId]);

  // Fallback: while a call is open, poll for status changes in case the realtime event is missed.
  // Ably is auxiliary (a dropped/misconfigured connection must not leave the customer stuck on
  // "Calling staff…" forever) — this guarantees "Staff is on the way" / "Served" still arrive.
  useEffect(() => {
    if (!call || !isOpen(call.status)) return;
    const ticketId = call.id;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiService.getServiceCalls();
        if (cancelled) return;
        const updated = ((res as any)?.data || []).find((c: Call) => c.id === ticketId);
        if (updated) { setCall(updated as Call); upsertCall(updated as Call); }
      } catch { /* non-fatal */ }
    };
    const iv = setInterval(poll, 8000); // fallback only — Ably delivers status changes instantly; keep DB read load low at scale
    return () => { cancelled = true; clearInterval(iv); };
  }, [call?.id, call?.status]);

  // Per-call chat thread: load + live-subscribe + poll for messages on the active call.
  const activeCallId = call && isOpen(call.status) ? call.id : null;
  useEffect(() => {
    if (!activeCallId) { setMessages([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await apiService.getCallMessages(activeCallId);
        if (!cancelled) setMessages(((res as any)?.data || []) as any);
      } catch { /* non-fatal */ }
    };
    load();
    let unsub = () => {};
    if (myUserId) {
      unsub = subscribeToChannel(
        `user:${myUserId}`,
        ['service_ticket_message:new'] as unknown as readonly string[],
        (_e, data) => {
          if (!data?.id || data.ticketId !== activeCallId) return;
          setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
        }
      );
    }
    const iv = setInterval(load, 8000); // fallback only — realtime carries new messages; lighten polling at scale
    return () => { cancelled = true; clearInterval(iv); try { unsub(); } catch { /* noop */ } };
  }, [activeCallId, myUserId]);

  const sendMsg = async () => {
    const text = msgInput.trim();
    if (!text || !activeCallId || sendingMsg) return;
    setSendingMsg(true);
    try {
      const res = await apiService.sendCallMessage(activeCallId, text);
      const m = (res as any)?.data;
      if (m?.id) setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
      setMsgInput('');
    } catch (e) {
      setError(isSessionMismatch(e) ? friendlyCallError(e) : (e instanceof Error ? e.message : 'Could not send message'));
    } finally {
      setSendingMsg(false);
    }
  };

  // Submit a pasted YouTube link as a song request. The resulting ServiceTicket flows through the
  // same realtime + polling the staff-call ticket uses; here we track its `songState` separately.
  const sendSong = async () => {
    const url = songUrl.trim();
    if (!url || songSending) return;
    if (!isYouTubeUrl(url)) {
      setSongError('Please paste a valid YouTube link (youtube.com or youtu.be).');
      return;
    }
    setSongSending(true);
    setSongError(null);
    try {
      const res = await apiService.createSongRequest(bookingId, url);
      const t = (res as any)?.data;
      if (t?.id) setSongTicket(t as SongTicket);
      setSongUrl('');
      setShowPreview(false);
      setSongOpen(false);
    } catch (e) {
      // 400 (invalid URL) / 429 (rate-limited) surface the server's message via request()'s Error.
      setSongError(e instanceof Error ? e.message : 'Could not request the song. Please try again.');
    } finally {
      setSongSending(false);
    }
  };

  // Live song status: the song ticket rides the existing `service_ticket:updated` realtime; adopt
  // any update matching our ticket id so the banner moves queued → now playing → played.
  useEffect(() => {
    if (!songTicket?.id || !myUserId) return;
    const id = songTicket.id;
    const unsub = subscribeToChannel(
      `user:${myUserId}`,
      ['service_ticket:updated'] as unknown as readonly string[],
      (_event, data) => {
        if (!data?.id || data.id !== id) return;
        setSongTicket((prev) => (prev ? { ...prev, ...data } : (data as SongTicket)));
      }
    );
    return () => { try { unsub(); } catch { /* noop */ } };
  }, [songTicket?.id, myUserId]);

  // Poll fallback (mirrors the staff-call poll) until the song has been played, in case realtime drops.
  useEffect(() => {
    if (!songTicket?.id || songTicket.songState === 'played') return;
    const id = songTicket.id;
    let cancelled = false;
    const iv = setInterval(async () => {
      try {
        const res = await apiService.getServiceCalls();
        if (cancelled) return;
        const updated = ((res as any)?.data || []).find((c: any) => c.id === id);
        if (updated) setSongTicket((prev) => (prev ? { ...prev, ...updated } : (updated as SongTicket)));
      } catch { /* non-fatal */ }
    }, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [songTicket?.id, songTicket?.songState]);

  const placeCall = async (callType?: string, note?: string, reasonLabel?: string) => {
    if (sending) return;
    setSending(callType || '__manual');
    setError(null);
    const send = (id: string) => apiService.createServiceCall({ bookingId: id, callType, note, reasonLabel });
    try {
      let res;
      try {
        res = await send(bookingId);
      } catch (e) {
        // The session no longer matches this booking (checked out, or the identity drifted). Rebuild
        // it once and send again — the guest just wanted a glass of water, not a diagnosis.
        if (!isSessionMismatch(e) || !onSessionInvalid) throw e;
        const freshId = await onSessionInvalid();
        if (!freshId) throw e;
        res = await send(freshId);
      }
      const t = (res as any)?.data;
      if (t?.id) { setCall(t as Call); upsertCall(t as Call); }
      if (note) setManualText('');
    } catch (e) {
      setError(friendlyCallError(e));
    } finally {
      setSending(null);
    }
  };

  const cancel = async () => {
    if (!call) return;
    try {
      await apiService.cancelServiceCall(call.id);
      upsertCall({ ...call, status: 'cancelled' });
      setCall(null);
    } catch (e) {
      setError(isSessionMismatch(e) ? friendlyCallError(e) : (e instanceof Error ? e.message : 'Could not cancel'));
    }
  };

  const active = call && isOpen(call.status) ? call : null;
  const preparing = active?.status === 'preparing';
  const acknowledged = active?.status === 'in_progress' || active?.status === 'approved';

  // Song queue + upvoting: when the song panel is open, show the branch's live queue so a guest can
  // 👍 songs they like (voted ids remembered per device so one guest = one vote per song).
  const [songQueue, setSongQueue] = useState<Array<{ id: string; mediaTitle?: string | null; mediaThumbnail?: string | null; votes: number; songState?: string | null }>>([]);
  const [voted, setVoted] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('songVotes') || '[]')); } catch { return new Set<string>(); }
  });
  useEffect(() => {
    if (!showSongPanel || !branchId) return;
    let alive = true;
    const load = () => apiService.getSongQueue(branchId).then((r) => { if (alive) setSongQueue(((r as any)?.data || [])); }).catch(() => { /* non-fatal */ });
    load();
    const iv = setInterval(load, 8000);
    return () => { alive = false; clearInterval(iv); };
  }, [showSongPanel, branchId]);
  const upvoteSong = async (id: string) => {
    if (voted.has(id)) return;
    setSongQueue((q) => q.map((s) => (s.id === id ? { ...s, votes: s.votes + 1 } : s)));
    const next = new Set(voted); next.add(id); setVoted(next);
    try { localStorage.setItem('songVotes', JSON.stringify([...next])); } catch { /* noop */ }
    try { await apiService.upvoteSong(id); } catch { /* optimistic — non-fatal */ }
  };

  // Don't discard the panel on an accidental backdrop tap while the guest is mid-entry (a typed
  // manual reason, chat message, or song link). Only a clean backdrop tap closes it.
  const onBackdrop = () => {
    if (manualText.trim() || msgInput.trim() || songUrl.trim()) return;
    onClose();
  };

  return (
    <div style={overlay} onClick={onBackdrop}>
      <style>{`@keyframes callStaffPulse{0%{box-shadow:0 0 0 0 rgba(249,115,22,0.55)}70%{box-shadow:0 0 0 14px rgba(249,115,22,0)}100%{box-shadow:0 0 0 0 rgba(249,115,22,0)}}`}</style>
      <div style={panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Call staff">
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <Bell size={18} color="#111827" />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: '#111827' }}>Call staff</div>
              <div style={subLabel}>{tableName || 'To your table'}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={iconBtn} aria-label="Close"><X size={20} /></button>
        </div>

        <div style={body}>
          {error && <div style={errorBox}>{error}</div>}

          {/* Song request status — persists across the call / no-call states once a song is queued. */}
          {songTicket && (
            <div style={songCard}>
              {songTicket.mediaThumbnail && <img src={songTicket.mediaThumbnail} alt="" style={songThumb} />}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={songTitle}>{songTicket.mediaTitle || 'Your song request'}</div>
                <div style={songMeta}>
                  {songStatusLabel(songTicket.songState)}
                  {typeof songTicket.votes === 'number' ? ` · ${songTicket.votes} ▲` : ''}
                </div>
              </div>
              {songTicket.songState === 'played' && (
                <button type="button" style={iconBtn} aria-label="Dismiss" onClick={() => setSongTicket(null)}><X size={18} /></button>
              )}
            </div>
          )}

          {showSongPanel ? (
            /* Focused song mode: only the song request UI (header + link input + preview) shows. */
            <div style={songSection}>
              <div style={songPanelHeader}>
                <button
                  type="button"
                  style={iconBtn}
                  aria-label="Back to call staff"
                  onClick={() => { setSongOpen(false); setSongError(null); setShowPreview(false); }}
                >
                  <ArrowLeft size={18} />
                </button>
                <span style={{ fontWeight: 800, color: '#6d28d9' }}>🎵 Request a song</span>
              </div>
              <div style={manualRow}>
                <input
                  style={manualInput}
                  placeholder="Paste a YouTube link"
                  value={songUrl}
                  disabled={songSending}
                  autoFocus
                  onChange={(e) => { setSongUrl(e.target.value); if (songError) setSongError(null); setShowPreview(false); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && songUrl.trim()) sendSong(); }}
                />
                <button
                  type="button"
                  style={{ ...manualBtn, background: '#7c3aed', opacity: songUrl.trim() && !songSending ? 1 : 0.5 }}
                  disabled={!songUrl.trim() || songSending}
                  onClick={sendSong}
                >
                  {songSending ? '…' : 'Send'}
                </button>
              </div>
              {/* Preview the pasted link before sending, so the customer confirms it's the right song. */}
              {songVideoId && !showPreview && (
                <button type="button" style={songPreviewBtn} onClick={() => setShowPreview(true)}>▶ Preview</button>
              )}
              {songVideoId && showPreview && (
                <YouTubePreview videoId={songVideoId} />
              )}
              {songError && <div style={{ ...errorBox, marginTop: 8, marginBottom: 0 }}>{songError}</div>}

              {/* Live queue — upvote songs you want to hear next. */}
              {songQueue.length > 0 && (
                <div style={queueWrap}>
                  <div style={queueTitle}>🎶 In the queue</div>
                  {songQueue.map((s) => (
                    <div key={s.id} style={queueRow}>
                      {s.mediaThumbnail
                        ? <img src={s.mediaThumbnail} alt="" style={queueThumb} />
                        : <div style={{ ...queueThumb, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🎵</div>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={queueSongTitle}>{s.mediaTitle || 'Song'}</div>
                        {s.songState === 'playing' && <div style={nowPlaying}>▶ Now playing</div>}
                      </div>
                      <button type="button" style={{ ...voteBtn, ...(voted.has(s.id) ? voteBtnOn : {}) }} disabled={voted.has(s.id)} onClick={() => upvoteSong(s.id)}>
                        👍 {s.votes}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Active call status + per-call chat, shown ABOVE the always-available picker so
                  the customer can still fire another reason or request a song. */}
              {active && (
            <div style={{ ...statusCard, ...(acknowledged ? statusCardAck : preparing ? statusCardPrep : statusCardRing) }}>
              <div style={{ fontSize: 40, lineHeight: 1, marginBottom: 8 }}>{acknowledged ? '🙋' : preparing ? '🍳' : '🔔'}</div>
              <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#111827' }}>
                {acknowledged ? 'Staff is on the way' : preparing ? 'Preparing your request' : 'Calling staff…'}
              </div>
              <div style={{ color: '#4b5563', marginTop: 6, fontSize: '0.92rem' }}>
                {acknowledged
                  ? `${active.assignedStaffName || 'A staff member'} is coming to ${tableName || 'your table'}.`
                  : preparing
                  ? `${active.assignedStaffName || 'The staff'} is preparing your request${active.requestText ? ` — ${active.requestText}` : ''}.`
                  : active.requestText}
              </div>
              {!acknowledged && !preparing && (
                <div style={{ ...pulseDot }} aria-hidden />
              )}
              {/* Live ETA / queue position while waiting; a reassurance once we've re-alerted the team. */}
              {!acknowledged && !preparing && typeof active.etaMinutes === 'number' && (
                <div style={etaChip}>
                  ⏱️ ≈{active.etaMinutes} min
                  {typeof active.aheadCount === 'number' && active.aheadCount > 0 ? ` · ${active.aheadCount} ahead of you` : ' · you’re next'}
                </div>
              )}
              {!acknowledged && !preparing && (active.priority || 0) > 0 && (
                <div style={escalatedNote}>⏰ Taking a little longer — we’ve alerted the team again.</div>
              )}
              <button type="button" style={cancelBtn} onClick={cancel}>Cancel call</button>

              {/* Per-call chat: clarify the request with staff (e.g. "normal or cold water?"). */}
              <div style={chatWrap} onClick={(e) => e.stopPropagation()}>
                <div style={chatTitle}>💬 Chat about this request</div>
                {/* Keyed on the SESSION (bookingId) so this shares one translation setting with the main chat. */}
                {translationAvailable && <TranslateBar enabled={translationAvailable} convId={bookingId} />}
                <div style={chatList}>
                  {messages.length === 0 ? (
                    <div style={chatEmpty}>Add a note if staff need more details.</div>
                  ) : (
                    messages.map((m) => {
                      const own = m.senderType === 'customer';
                      return (
                        <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: own ? 'flex-end' : 'flex-start' }}>
                          {!own && <div style={chatSender}>{m.senderName}</div>}
                          <div style={{ ...chatBubble, ...(own ? chatBubbleOwn : chatBubbleOther) }}>{m.messageText}</div>
                          <TranslateInline text={m.messageText} enabled={translationAvailable} branchId={branchId} isIncoming={!own} convId={bookingId} />
                        </div>
                      );
                    })
                  )}
                  <div ref={chatEndRef} />
                </div>
                <div style={chatInputRow}>
                  <input
                    style={chatInput}
                    placeholder="Message staff…"
                    value={msgInput}
                    maxLength={500}
                    onChange={(e) => setMsgInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && msgInput.trim()) sendMsg(); }}
                  />
                  <VoiceInput lang={getInputLang(bookingId)} baseText={msgInput} onText={(t) => setMsgInput(t)} disabled={sendingMsg} />
                  <button type="button" style={{ ...chatSendBtn, opacity: msgInput.trim() && !sendingMsg ? 1 : 0.5 }} disabled={!msgInput.trim() || sendingMsg} onClick={sendMsg}>
                    {sendingMsg ? '…' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
              )}
              {!active && call && call.status === 'served' && (
            <div style={{ ...statusCard, ...statusCardDone }}>
              {fbResult ? (
                <>
                  <div style={{ fontSize: 34 }}>🙏</div>
                  <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#111827' }}>Thanks for your feedback!</div>
                  {fbResult.reviewUrl && (
                    <>
                      <div style={{ color: '#4b5563', fontSize: '0.9rem', marginTop: 4 }}>Loved it? A quick Google review means a lot 💛</div>
                      <a href={fbResult.reviewUrl} target="_blank" rel="noreferrer" style={reviewBtn}>⭐ Leave a Google review</a>
                    </>
                  )}
                  <button type="button" style={{ ...cancelBtn, background: '#111827' }} onClick={() => { setCall(null); resetFeedback(); }}>Done</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 38, marginBottom: 2 }}><Check size={34} /></div>
                  <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#111827' }}>Served ✨</div>
                  <div style={{ color: '#4b5563', marginTop: 4, fontSize: '0.9rem' }}>How was the service?</div>
                  <div style={starRow}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" style={starBtn} onClick={() => setFbRating(n)} aria-label={`${n} star${n > 1 ? 's' : ''}`}>
                        <span style={{ fontSize: 30, filter: n <= fbRating ? 'none' : 'grayscale(1)', opacity: n <= fbRating ? 1 : 0.35 }}>⭐</span>
                      </button>
                    ))}
                  </div>
                  {fbRating > 0 && (
                    <>
                      <textarea
                        style={fbInput}
                        placeholder={fbRating >= 4 ? 'Anything you loved? (optional)' : 'Sorry! What went wrong? (optional)'}
                        value={fbComment}
                        maxLength={500}
                        onChange={(e) => setFbComment(e.target.value)}
                      />
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', marginTop: 2 }}>
                        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Add a tip?</span>
                        {[0, 10, 20, 50].map((t) => (
                          <button key={t} type="button" style={{ ...tipChip, ...((fbTip || 0) === t ? tipChipOn : {}) }} onClick={() => setFbTip(t || null)}>
                            {t === 0 ? 'No tip' : `+${t}`}
                          </button>
                        ))}
                      </div>
                      <button type="button" style={{ ...reviewBtn, opacity: fbSubmitting ? 0.6 : 1 }} disabled={fbSubmitting} onClick={submitFeedback}>
                        {fbSubmitting ? 'Sending…' : 'Submit'}
                      </button>
                    </>
                  )}
                  <button type="button" style={{ ...cancelBtn, background: '#111827' }} onClick={() => setCall(null)}>Call again</button>
                </>
              )}
            </div>
              )}
              <div style={prompt}>What do you need? Tap once — the staff at your table get it instantly.</div>
              {loadingReasons ? (
                <div style={{ ...prompt, color: '#9ca3af' }}>Loading options…</div>
              ) : (
              <div style={grid}>
                {REASONS.map((r) => (
                  <button
                    key={r.type}
                    type="button"
                    style={{ ...reasonBtn, ...(r.type === primaryType ? reasonBtnPrimary : {}), opacity: sending && sending !== r.type ? 0.5 : 1 }}
                    disabled={!!sending}
                    onClick={() => placeCall(r.type, undefined, r.label)}
                  >
                    <span style={{ fontSize: 26 }}>{r.emoji}</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700 }}>{sending === r.type ? 'Calling…' : r.label}</span>
                  </button>
                ))}
              </div>
              )}

              {/* Manual reason — anything the quick chips don't cover. */}
              <div style={manualRow}>
                <input
                  style={manualInput}
                  placeholder="Or type your own request…"
                  value={manualText}
                  maxLength={200}
                  disabled={!!sending}
                  onChange={(e) => setManualText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && manualText.trim()) placeCall(undefined, manualText.trim()); }}
                />
                <button
                  type="button"
                  style={{ ...manualBtn, opacity: manualText.trim() && !sending ? 1 : 0.5 }}
                  disabled={!manualText.trim() || !!sending}
                  onClick={() => placeCall(undefined, manualText.trim())}
                >
                  {sending === '__manual' ? '…' : 'Send'}
                </button>
              </div>

              {/* Quick song request — opens the focused song panel; only when `quick_songs` is on. */}
              {songsEnabled && (
                <div style={songSection}>
                  <button type="button" style={songActionBtn} onClick={() => { setSongOpen(true); setSongError(null); setShowPreview(false); }}>
                    <span style={{ fontSize: 20 }}>🎵</span>
                    <span>Request a song</span>
                  </button>
                </div>
              )}

              {/* Your requests — this guest's tickets for THIS booking, most-recent first, live-updated. */}
              {allCalls.length > 0 && (
                <div style={requestsSection}>
                  <div style={requestsTitle}>Your requests</div>
                  <div style={requestsList}>
                    {[...allCalls]
                      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .map((c) => {
                        const pill = statusPill(c.status);
                        // Tapping an OPEN request makes it the active one — so its status card + chat
                        // come to the front. This is how you reach the chat of an EARLIER open call
                        // after firing another reason (each call keeps its own thread).
                        const open = isOpen(c.status);
                        const isActive = call?.id === c.id;
                        return (
                          <div
                            key={c.id}
                            style={{
                              ...requestRow,
                              ...(open ? { cursor: 'pointer' } : {}),
                              ...(isActive ? { borderColor: '#6ee7b7', background: '#ecfdf5' } : {}),
                            }}
                            onClick={open ? () => setCall(c) : undefined}
                            role={open ? 'button' : undefined}
                            title={open ? 'Open this request' : undefined}
                          >
                            <span style={requestRowText}>{c.requestText || 'Request'}</span>
                            <span style={{ ...requestPill, background: pill.bg, color: pill.color, borderColor: pill.border }}>{pill.label}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1300 };
const panel: React.CSSProperties = { background: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16, width: 'min(520px, 100vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -6px 30px rgba(0,0,0,0.25)' };
const header: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #e5e7eb', gap: 8 };
const subLabel: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const body: React.CSSProperties = { padding: 16, overflowY: 'auto' };
const prompt: React.CSSProperties = { fontSize: '0.9rem', color: '#374151', marginBottom: 14, textAlign: 'center' };
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 };
const reasonBtn: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '16px 8px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 14, cursor: 'pointer', color: '#111827' };
const reasonBtnPrimary: React.CSSProperties = { background: '#ecfdf5', border: '1px solid #6ee7b7', gridColumn: '1 / -1', flexDirection: 'row', justifyContent: 'center', padding: '16px', fontSize: '1rem' };
const manualRow: React.CSSProperties = { display: 'flex', gap: 8, marginTop: 12 };
const manualInput: React.CSSProperties = { flex: 1, boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 10, padding: '10px 12px', fontSize: '0.92rem', outline: 'none', color: '#111827', background: '#fff' };
const manualBtn: React.CSSProperties = { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
const statusCard: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '22px 16px', borderRadius: 16, position: 'relative' };
const statusCardRing: React.CSSProperties = { background: '#fff7ed', border: '1px solid #fed7aa' };
const statusCardAck: React.CSSProperties = { background: '#eff6ff', border: '1px solid #bfdbfe' };
const statusCardPrep: React.CSSProperties = { background: '#fffbeb', border: '1px solid #fde68a' };
const chatWrap: React.CSSProperties = { width: '100%', marginTop: 14, borderTop: '1px solid #e5e7eb', paddingTop: 12, textAlign: 'left' };
const chatTitle: React.CSSProperties = { fontSize: '0.8rem', fontWeight: 700, color: '#374151', marginBottom: 8 };
const chatList: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 160, overflowY: 'auto', marginBottom: 8 };
const chatEmpty: React.CSSProperties = { fontSize: '0.8rem', color: '#9ca3af', padding: '4px 0' };
const chatSender: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 700, color: '#f97316', margin: '0 4px 2px' };
const chatBubble: React.CSSProperties = { maxWidth: '82%', padding: '7px 11px', fontSize: '0.88rem', lineHeight: 1.35, wordBreak: 'break-word' };
const chatBubbleOwn: React.CSSProperties = { background: '#f97316', color: '#fff', borderRadius: '14px 14px 4px 14px' };
const chatBubbleOther: React.CSSProperties = { background: '#f3f4f6', color: '#111827', borderRadius: '14px 14px 14px 4px' };
const chatInputRow: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center' };
const chatInput: React.CSSProperties = { flex: 1, border: '1px solid #d1d5db', borderRadius: 9999, padding: '9px 14px', fontSize: '0.9rem', outline: 'none' };
const chatSendBtn: React.CSSProperties = { background: '#f97316', color: '#fff', border: 'none', borderRadius: 9999, padding: '9px 16px', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' };
const statusCardDone: React.CSSProperties = { background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#065f46' };
const pulseDot: React.CSSProperties = { width: 10, height: 10, borderRadius: '50%', background: '#f97316', marginTop: 12, boxShadow: '0 0 0 0 rgba(249,115,22,0.6)', animation: 'callStaffPulse 1.4s infinite' };
const etaChip: React.CSSProperties = { marginTop: 12, background: 'rgba(255,107,53,0.12)', color: '#e0531f', fontWeight: 800, fontSize: '0.9rem', padding: '6px 14px', borderRadius: 9999, border: '1px solid rgba(255,107,53,0.28)' };
const escalatedNote: React.CSSProperties = { marginTop: 8, color: '#b45309', fontSize: '0.85rem', fontWeight: 600 };
const starRow: React.CSSProperties = { display: 'flex', gap: 4, marginTop: 8 };
const starBtn: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 0 };
const fbInput: React.CSSProperties = { width: '100%', boxSizing: 'border-box', marginTop: 10, border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px', fontSize: '0.9rem', outline: 'none', color: '#111827', background: '#fff', resize: 'none', minHeight: 46 };
const tipChip: React.CSSProperties = { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 9999, padding: '5px 12px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' };
const tipChipOn: React.CSSProperties = { background: 'rgba(255,107,53,0.12)', color: '#e0531f', borderColor: 'rgba(255,107,53,0.4)' };
const reviewBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 12, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontWeight: 800, cursor: 'pointer', textDecoration: 'none' };
const queueWrap: React.CSSProperties = { marginTop: 14, borderTop: '1px solid #eef0f2', paddingTop: 10 };
const queueTitle: React.CSSProperties = { fontSize: '0.82rem', fontWeight: 800, color: '#6d28d9', marginBottom: 8 };
const queueRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' };
const queueThumb: React.CSSProperties = { width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 };
const queueSongTitle: React.CSSProperties = { fontSize: '0.85rem', fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const nowPlaying: React.CSSProperties = { fontSize: '0.72rem', fontWeight: 800, color: '#059669' };
const voteBtn: React.CSSProperties = { background: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe', borderRadius: 9999, padding: '5px 11px', fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' };
const voteBtnOn: React.CSSProperties = { background: '#6d28d9', color: '#fff', borderColor: '#6d28d9', cursor: 'default' };
const cancelBtn: React.CSSProperties = { marginTop: 16, background: '#374151', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' };
const errorBox: React.CSSProperties = { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', fontSize: '0.82rem', marginBottom: 12 };
const songSection: React.CSSProperties = { marginTop: 12, borderTop: '1px solid #f0e9fb', paddingTop: 12 };
const songActionBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px 8px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 14, cursor: 'pointer', color: '#6d28d9', fontWeight: 700, fontSize: '0.92rem' };
const songCard: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 12, padding: 10, marginBottom: 12 };
const songThumb: React.CSSProperties = { width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 };
const songTitle: React.CSSProperties = { fontWeight: 800, fontSize: '0.9rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const songMeta: React.CSSProperties = { fontSize: '0.82rem', color: '#6d28d9', marginTop: 2 };
const songPanelHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 };
const songPreviewBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '8px 14px', color: '#6d28d9', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' };
const requestsSection: React.CSSProperties = { marginTop: 14, borderTop: '1px solid #e5e7eb', paddingTop: 12 };
const requestsTitle: React.CSSProperties = { fontSize: '0.8rem', fontWeight: 700, color: '#374151', marginBottom: 8 };
const requestsList: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const requestRow: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#f9fafb', border: '1px solid #eef0f2', borderRadius: 10, padding: '8px 10px' };
const requestRowText: React.CSSProperties = { fontSize: '0.85rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 };
const requestPill: React.CSSProperties = { fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px', borderRadius: 9999, border: '1px solid', whiteSpace: 'nowrap', flexShrink: 0 };

export default CallStaff;
